import { Button, Checkbox, Collapse, Group, Loader, Stack, Text, TextInput } from '@mantine/core'
import { useDocumentTitle, useInViewport } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconChevronDown, IconDownload, IconFileOff, IconLinkOff, IconPrinter, IconWritingSign } from '@tabler/icons-react'
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  companyQueryKey,
  isApiError,
  type NewSignature,
  SignatureCapture,
  SignatureFilesProvider,
  SignatureImage,
  usePrint,
} from '@tnn/app-kit'
import { formatDate, formatDateTime, formatMoney } from '@tnn/core'
import { BillingNoteDocument, InvoiceDocument, PaymentDocument } from '@tnn/documents'
import { termsPoints } from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { TermsPoint } from '@tnn/transport'
import { WaybillDocument } from '@tnn/transport/waybill-document'
import { EmptyState, InitialsAvatar, StatusPill } from '@tnn/ui'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type LinkedDocument, signApi } from './api'
import { savePdf } from './pdf'
import { ScaledPage } from './sheet'
import classes from './sign.module.css'

/**
 * A document's signing link, opened by someone with no account: the document as it prints, a box to sign it, then a PDF
 * to keep — a customer receiving an accounting document, a sender accepting a bill's shipping terms, which they read in
 * full and tick before signing. The page renders the apps' own documents (@tnn/documents, the bill from
 * @tnn/transport) from what the link sends, their queries answered from it.
 */

const s = (key: string) => `sign:${key}`

/** The document's queries, answered from what the link sent: nothing is fetched behind it */
function answered(data: LinkedDocument) {
  const client = new QueryClient({
    defaultOptions: { queries: { staleTime: Number.POSITIVE_INFINITY, retry: false, refetchOnWindowFocus: false, enabled: false } },
  })
  client.setQueryData(companyQueryKey, data.company)
  if (data.customer)
    client.setQueryData(['r', 'accounting.customer', 'record', String(data.customer.id)], { item: data.customer, refs: {} })
  for (const item of data.invoices ?? []) client.setQueryData(['r', 'accounting.invoice', 'record', String(item.id)], { item, refs: {} })
  client.setQueryData(['signatures', data.subject, String(data.record.id)], data.signatures)
  return client
}

/** The bill as it prints, its original only: the sender's copy, with what they signed on it */
const theirCopy = { original: true, copies: 0 }

function Document({ data }: { data: LinkedDocument }) {
  const props = { record: data.record, refs: data.refs }
  if (data.subject === 'tms.waybill') {
    const signed = data.signatures.find((x) => x.role === data.link.role)
    return <WaybillDocument {...props} terms={data.terms ?? undefined} senderSignature={signed} paper="a5" sheets={theirCopy} />
  }
  if (data.subject === 'accounting.invoice') return <InvoiceDocument {...props} />
  if (data.subject === 'accounting.billingNote') return <BillingNoteDocument {...props} />
  return <PaymentDocument {...props} />
}

/** A bill's shipping terms as its sender reads them before signing: the key points, the whole text a tap away */
function TermsCard({ terms }: { terms: NonNullable<LinkedDocument['terms']> }) {
  const { t } = useTranslation()
  const [full, setFull] = useState(false)
  return (
    <section className={classes.card}>
      <h2 className={classes.cardTitle}>{t('tms:terms.print.heading', { number: String(terms.number ?? '') })}</h2>
      <ol className={classes.points}>
        {termsPoints(terms.summary).map((point) => (
          <li key={point}>
            <TermsPoint point={point} />
          </li>
        ))}
      </ol>
      <Button
        variant="subtle"
        radius="xl"
        className={classes.more}
        rightSection={<IconChevronDown size={18} className={classes.chevron} data-open={full || undefined} />}
        onClick={() => setFull((open) => !open)}
      >
        {t(s(full ? 'terms.hide' : 'terms.full'))}
      </Button>
      <Collapse expanded={full}>
        <div className={classes.fullTerms}>{String(terms.body ?? '')}</div>
      </Collapse>
    </section>
  )
}

/**
 * Who signs and their signature, sent once: a bill's sender (their name filled in, which whoever signs for them
 * changes) ticks that they read and accept its terms first
 */
function SignCard({ token, data, cardRef }: { token: string; data: LinkedDocument; cardRef: (node: HTMLDivElement | null) => void }) {
  const { t } = useTranslation()
  const client = useQueryClient()
  const bill = data.subject === 'tms.waybill'
  const terms = String(data.terms?.number ?? '')
  const [name, setName] = useState(bill ? String(data.record.senderName ?? '') : '')
  const [title, setTitle] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [signature, setSignature] = useState<NewSignature | null>(null)
  const [errors, setErrors] = useState<{ name?: string; signature?: string; accepted?: string }>({})
  const [sending, setSending] = useState(false)
  const words = (key: string) => t(s(bill ? `terms.${key}` : key))
  const send = async () => {
    const missing = {
      name: name.trim() ? undefined : t('form.required'),
      signature: signature ? undefined : t(s('needSignature')),
      accepted: !bill || accepted ? undefined : t(s('terms.needAccept')),
    }
    setErrors(missing)
    if (missing.name || missing.signature || missing.accepted || !signature) return
    setSending(true)
    try {
      const signed = await signApi.sign(token, {
        name: name.trim(),
        title: title.trim() || null,
        method: signature.method,
        image: signature.blob,
      })
      client.setQueryData(['public', 'sign', token], signed)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      const issue = isApiError(error) ? error.issues?.[0] : undefined
      if (issue?.path.at(-1) === 'name') setErrors({ name: issue.message })
      else notifications.show({ color: 'red', message: error instanceof Error ? error.message : String(error) })
    } finally {
      setSending(false)
    }
  }
  return (
    <section ref={cardRef} className={classes.card} id="sign">
      <Stack gap={4}>
        <h2 className={classes.cardTitle}>{words('signTitle')}</h2>
        <Text size="sm" c="dimmed">
          {words('signHint')}
        </Text>
      </Stack>
      <TextInput
        size="md"
        label={words('name')}
        withAsterisk
        autoComplete="name"
        value={name}
        error={errors.name}
        onChange={(e) => {
          setName(e.currentTarget.value)
          setErrors((current) => ({ ...current, name: undefined }))
        }}
      />
      <TextInput size="md" label={t(s('title'))} value={title} onChange={(e) => setTitle(e.currentTarget.value)} />
      {bill && (
        <Checkbox
          size="md"
          checked={accepted}
          error={errors.accepted}
          label={t('tms:terms.sign.agree', { name: name.trim() || '…', number: terms })}
          onChange={(e) => {
            setAccepted(e.currentTarget.checked)
            setErrors((current) => ({ ...current, accepted: undefined }))
          }}
        />
      )}
      <Stack gap={6}>
        <SignatureCapture
          onChange={(next) => {
            setSignature(next)
            if (next) setErrors((e) => ({ ...e, signature: undefined }))
          }}
        />
        {errors.signature && (
          <Text size="sm" c="var(--mantine-color-error)">
            {errors.signature}
          </Text>
        )}
      </Stack>
      <Text className={classes.legal}>{t(s('legal'))}</Text>
      <Button size="lg" radius="xl" leftSection={<IconWritingSign size={20} />} loading={sending} onClick={send}>
        {words('submit')}
      </Button>
    </section>
  )
}

/** What the document is for, at a glance: how much, by when */
function facts(data: LinkedDocument, locale: 'th' | 'en', t: (key: string, values?: Record<string, unknown>) => string) {
  const r = data.record
  const amounts = { 'accounting.invoice': r.total, 'accounting.billingNote': r.amount, 'accounting.payment': r.received ?? r.settled }
  const amount = data.subject === 'tms.waybill' ? r.fee : amounts[data.subject]
  const due = data.subject === 'accounting.invoice' || data.subject === 'accounting.billingNote' ? (r.dueDate as string | null) : null
  return [
    amount != null ? t(s('amount'), { amount: formatMoney(Number(amount)) }) : null,
    due ? t(s('due'), { date: formatDate(due, { locale, style: 'long' }) }) : null,
  ].filter((v): v is string => !!v)
}

function SignedDocument({ token, data }: { token: string; data: LinkedDocument }) {
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const sheet = useRef<HTMLDivElement>(null)
  const number = String(data.record.number ?? '')
  const bill = data.subject === 'tms.waybill'
  const docName = bill ? t('tms:waybill.title') : t(`accounting:${data.subject.split('.')[1]}.documentTitle`)
  const printer = usePrint({ title: `${docName} ${number}`, pageLabel: t('print.page') })
  const client = useMemo(() => answered(data), [data])
  const files = useMemo(() => (fileId: string) => `/api/public/sign/${encodeURIComponent(token)}/files/${fileId}`, [token])
  const { ref: cardRef, inViewport } = useInViewport()
  const [making, setMaking] = useState(false)
  const signed = data.signatures.find((x) => x.role === data.link.role)
  const open = data.link.status === 'open' && !signed
  const company = data.company?.name ?? ''
  useDocumentTitle(`${docName} ${number} · ${company}`)

  const download = async () => {
    if (!sheet.current) return
    setMaking(true)
    try {
      await savePdf(sheet.current, `${number || docName}`, t('print.page'))
    } catch {
      notifications.show({ color: 'red', message: t(s('failed')) })
    } finally {
      setMaking(false)
    }
  }
  const pdfButton = (primary: boolean) => (
    <Button
      size="md"
      radius="xl"
      variant={primary ? 'filled' : 'default'}
      leftSection={<IconDownload size={18} />}
      loading={making}
      onClick={download}
    >
      {making ? t(s('making')) : t(s('download'))}
    </Button>
  )

  return (
    <div className={classes.page}>
      <header className={classes.top}>
        <InitialsAvatar name={company} size={36} />
        <span className={classes.company}>{company}</span>
      </header>
      <div className={classes.column}>
        <div className={classes.hero}>
          <Text size="sm" c="dimmed">
            {t(s('sentBy'), { company })}
          </Text>
          <Stack gap={2}>
            <h1 className={classes.docTitle}>{docName}</h1>
            <Text size="md" fw={500} className="tnn-num">
              {number}
            </Text>
          </Stack>
          <div className={classes.facts}>
            {facts(data, locale, t).map((fact, i) => (
              <span key={fact} className={i === 0 ? classes.amount : undefined}>
                {fact}
              </span>
            ))}
            {!signed && <StatusPill tone="warning" label={t(s('unsigned'))} />}
          </div>
          <Group gap="sm" mt={4}>
            {signed ? (
              <>
                {pdfButton(true)}
                <Button size="md" radius="xl" variant="default" leftSection={<IconPrinter size={18} />} onClick={() => printer.print()}>
                  {t(s('print'))}
                </Button>
              </>
            ) : (
              pdfButton(false)
            )}
          </Group>
        </div>

        {signed && (
          <section className={classes.card}>
            <div className={classes.done}>
              <span className={classes.doneIcon}>
                <IconCheck size={24} />
              </span>
              <Stack gap={2}>
                <Text fw={500}>{t(s(bill ? 'terms.signed' : 'signed'))}</Text>
                <Text size="sm" c="dimmed">
                  {t(s('signedBy'), { name: signed.name, at: formatDateTime(signed.signedAt, { locale, style: 'short' }) })}
                </Text>
              </Stack>
            </div>
            {signed.method === 'paper' ? (
              <Text size="sm" c="dimmed">
                {t('signing.onPaper')}
              </Text>
            ) : (
              <SignatureImage src={files(signed.fileId)} />
            )}
            <Text className={classes.legal}>{t(s('keep'), { date: formatDate(data.link.expiresAt, { locale, style: 'long' }) })}</Text>
          </section>
        )}

        <QueryClientProvider client={client}>
          <SignatureFilesProvider value={files}>
            <ScaledPage sheet={sheet} printRef={printer.ref} short={bill}>
              <Document data={data} />
            </ScaledPage>
          </SignatureFilesProvider>
        </QueryClientProvider>

        {bill && data.terms && <TermsCard terms={data.terms} />}
        {open && <SignCard token={token} data={data} cardRef={cardRef} />}
      </div>
      {open && (
        <div className={classes.dock} data-hidden={inViewport || undefined}>
          <Button
            size="lg"
            radius="xl"
            leftSection={<IconWritingSign size={20} />}
            onClick={() => document.getElementById('sign')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t(s('toSign'))}
          </Button>
        </div>
      )}
    </div>
  )
}

/** `/<token>`: the document behind a signing link, or why it does not open (no token: no link) */
export function SignDocumentPage({ token }: { token: string }) {
  const { t } = useTranslation()
  const query = useQuery({ queryKey: ['public', 'sign', token], queryFn: () => signApi.document(token), enabled: !!token })
  if (query.data) return <SignedDocument token={token} data={query.data} />
  const expired = isApiError(query.error) && (query.error.code as string) === 'SIGN_LINK_EXPIRED'
  const invalid = !token || (isApiError(query.error) && (query.error.code as string) === 'SIGN_LINK_INVALID')
  return (
    <div className={classes.page}>
      <div className={classes.state}>
        {token && query.isPending ? (
          <Loader type="dots" />
        ) : (
          <EmptyState
            icon={expired ? IconFileOff : IconLinkOff}
            title={t(s(expired ? 'expired' : invalid ? 'invalid' : 'failed'))}
            body={expired ? t(s('expiredHint')) : invalid ? t(s('invalidHint')) : undefined}
            actions={
              !expired && !invalid ? (
                <Button variant="light" onClick={() => query.refetch()}>
                  {t('actions.retry')}
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  )
}
